/**
 * Phase 3 — ConstraintEditor
 *
 * Three sub-sections:
 *   • Range constraints (auto-mirror of selected variables, override toggle)
 *   • Relational constraints (left operator right; pluggable rows)
 *   • Cost budget (single slider)
 *
 * Plus a model-confidence floor selector. The shape this writes to
 * the request body matches `OptimisationConstraints` exactly.
 */
import { useMemo } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TIERS = ["validated", "provisional", "experimental"];
const RELATIONAL_OPS = ["<", "<=", ">", ">=", "==", "!="];

export default function ConstraintEditor({
    constraints,
    onChange,
    selectedVariables = {},
}) {
    const cons = constraints || {
        range_constraints: [],
        relational_constraints: [],
        cost_constraints: [],
        max_design_cost: 0.55,
        confidence_floor: "provisional",
    };

    const patch = (next) => onChange({ ...cons, ...next });

    const variablePaths = useMemo(
        () => Object.keys(selectedVariables || {}),
        [selectedVariables],
    );

    // ---- relational constraints ----
    const addRelational = () => {
        const next = [...(cons.relational_constraints || [])];
        next.push({
            left_path: variablePaths[0] || "",
            op: "<=",
            right_path: variablePaths[1] || "",
            right_value: null,
        });
        patch({ relational_constraints: next });
    };
    const updateRelational = (idx, fields) => {
        const next = [...(cons.relational_constraints || [])];
        next[idx] = { ...next[idx], ...fields };
        patch({ relational_constraints: next });
    };
    const removeRelational = (idx) => {
        const next = [...(cons.relational_constraints || [])];
        next.splice(idx, 1);
        patch({ relational_constraints: next });
    };

    return (
        <div className="space-y-4" data-testid="constraint-editor">
            {/* Range — read-only mirror */}
            <div className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-3 space-y-2">
                <h3 className="text-[12px] uppercase tracking-wider text-ux-text-3">
                    Range constraints
                </h3>
                <p className="text-[11px] text-ux-text-3">
                    Each selected variable's min/max acts as its range constraint
                    automatically. Edit the band on the variable itself.
                </p>
                {variablePaths.length === 0 ? (
                    <p className="text-[12px] text-ux-text-3 italic">
                        No variables selected.
                    </p>
                ) : (
                    <ul className="space-y-1">
                        {variablePaths.map((p) => {
                            const v = selectedVariables[p];
                            return (
                                <li
                                    key={p}
                                    className="text-[12px] flex items-center gap-2 font-mono"
                                    data-testid={`range-row-${p}`}
                                >
                                    <span className="text-ux-text-2 truncate">{p}</span>
                                    <span className="text-ux-text-3">∈</span>
                                    <span className="tabular-nums text-ux-text-1">
                                        [{v.min}, {v.max}]
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {/* Relational */}
            <div className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-3 space-y-2">
                <div className="flex items-center justify-between">
                    <h3 className="text-[12px] uppercase tracking-wider text-ux-text-3">
                        Relational rules
                    </h3>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={addRelational}
                        disabled={variablePaths.length === 0}
                        data-testid="constraint-add-relational"
                        className="h-7 text-[11px]"
                    >
                        <Plus className="w-3 h-3 mr-1" /> Add rule
                    </Button>
                </div>
                {(cons.relational_constraints || []).length === 0 && (
                    <p className="text-[11px] text-ux-text-3 italic">
                        No relational rules. Add one to constrain pairs of variables
                        (e.g. interceptor.max_speed_mps {">"} target.max_speed_mps).
                    </p>
                )}
                {(cons.relational_constraints || []).map((row, idx) => (
                    <div
                        key={idx}
                        className="flex items-center gap-2 flex-wrap"
                        data-testid={`relational-row-${idx}`}
                    >
                        <Select
                            value={row.left_path || ""}
                            onValueChange={(v) => updateRelational(idx, { left_path: v })}
                        >
                            <SelectTrigger className="w-[200px] bg-ux-bg-2 h-7 text-[11px]">
                                <SelectValue placeholder="left path" />
                            </SelectTrigger>
                            <SelectContent>
                                {variablePaths.map((p) => (
                                    <SelectItem key={p} value={p}>
                                        {p}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select
                            value={row.op}
                            onValueChange={(v) => updateRelational(idx, { op: v })}
                        >
                            <SelectTrigger className="w-[60px] bg-ux-bg-2 h-7 text-[11px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {RELATIONAL_OPS.map((o) => (
                                    <SelectItem key={o} value={o}>
                                        {o}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select
                            value={row.right_path || ""}
                            onValueChange={(v) =>
                                updateRelational(idx, { right_path: v, right_value: null })
                            }
                        >
                            <SelectTrigger className="w-[200px] bg-ux-bg-2 h-7 text-[11px]">
                                <SelectValue placeholder="right path or numeric" />
                            </SelectTrigger>
                            <SelectContent>
                                {variablePaths.map((p) => (
                                    <SelectItem key={p} value={p}>
                                        {p}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Input
                            type="number"
                            value={row.right_value ?? ""}
                            onChange={(e) =>
                                updateRelational(idx, {
                                    right_value: e.target.value === "" ? null : parseFloat(e.target.value),
                                    right_path: e.target.value === "" ? row.right_path : null,
                                })
                            }
                            placeholder="or value"
                            className="w-[100px] bg-ux-bg-2 h-7 px-2 text-[11px]"
                        />
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeRelational(idx)}
                            data-testid={`relational-remove-${idx}`}
                            className="h-7"
                        >
                            <Trash2 className="w-3 h-3" />
                        </Button>
                    </div>
                ))}
            </div>

            {/* Cost budget */}
            <div className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-3 space-y-3">
                <div className="flex items-center justify-between">
                    <h3 className="text-[12px] uppercase tracking-wider text-ux-text-3">
                        Design-cost budget
                    </h3>
                    <span
                        className="text-[12px] text-ux-text-1 font-mono tabular-nums"
                        data-testid="constraint-cost-readout"
                    >
                        ≤ {(cons.max_design_cost ?? 0.55).toFixed(2)}
                    </span>
                </div>
                <Slider
                    min={0.1}
                    max={2.0}
                    step={0.05}
                    value={[cons.max_design_cost ?? 0.55]}
                    onValueChange={([v]) => patch({ max_design_cost: v })}
                    data-testid="constraint-cost-slider"
                />
                <p className="text-[11px] text-ux-text-3">
                    Aggregate weighted-sum of variable deltas. 0 = baseline; 1 ≈ a
                    significant configuration change. Candidates above this budget
                    are dropped before evaluation.
                </p>
            </div>

            {/* Confidence floor */}
            <div className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-3 space-y-2">
                <Label
                    htmlFor="constraint-confidence"
                    className="text-[12px] uppercase tracking-wider text-ux-text-3"
                >
                    Model confidence floor
                </Label>
                <Select
                    value={cons.confidence_floor || "provisional"}
                    onValueChange={(v) => patch({ confidence_floor: v })}
                >
                    <SelectTrigger
                        id="constraint-confidence"
                        className="bg-ux-bg-2 max-w-[260px] h-8"
                        data-testid="constraint-confidence-trigger"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {TIERS.map((t) => (
                            <SelectItem key={t} value={t}>
                                {t.charAt(0).toUpperCase() + t.slice(1)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <p className="text-[11px] text-ux-text-3">
                    Variables with model-confidence below this floor are dropped
                    from the search even if the operator selected them.
                </p>
            </div>

            {/* Strict baseline */}
            <div className="flex items-center justify-between rounded-md border border-ux-border-1 bg-ux-bg-1 p-3">
                <div>
                    <Label className="text-[12px] uppercase tracking-wider text-ux-text-3 cursor-pointer">
                        Strict baseline matching
                    </Label>
                    <p className="text-[11px] text-ux-text-3 mt-1">
                        Reject candidates that deviate beyond ±2σ from the baseline
                        in unselected dimensions. Adds determinism at the cost of
                        more rejections.
                    </p>
                </div>
                <Switch
                    checked={!!cons.strict_baseline}
                    onCheckedChange={(v) => patch({ strict_baseline: v })}
                    data-testid="constraint-strict-baseline"
                />
            </div>
        </div>
    );
}
